/**
 * PropertyVoiceFab — Voice command center del cantiere immobile.
 *
 * iter v29 r085 · Unifica:
 *   • Modifica campi annuncio (update_fields)
 *   • Lavoro foto (enhance / stage / restyle / delete / cover)
 *   • Genera materiali (post / reel / brochure / publish)
 *
 * L'AI classifica l'intent del comando vocale e il modal lo esegue subito
 * senza chiedere conferma (modalità auto-run scelta dall'utente).
 */
import { useEffect, useRef, useState } from "react";
import { Microphone } from "@phosphor-icons/react";
import VoiceCommandModal from "@/components/VoiceCommandModal";

export default function PropertyVoiceFab({ property, photos = [], onUpdated }) {
  const [open, setOpen] = useState(false);
  // iter v29 r082 — UX: parte espanso 6s, poi cerchio piccolo, scroll-aware
  const [collapsed, setCollapsed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const collapseTimer = useRef(null);
  const lastY = useRef(0);

  useEffect(() => {
    collapseTimer.current = setTimeout(() => setCollapsed(true), 6000);
    return () => {
      if (collapseTimer.current) clearTimeout(collapseTimer.current);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      const dy = y - lastY.current;
      if (Math.abs(dy) < 12) return;
      if (y < 60) setHidden(false);
      else if (dy > 0) setHidden(true);
      else setHidden(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!property?.id) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (collapsed) {
            setCollapsed(false);
            if (collapseTimer.current) clearTimeout(collapseTimer.current);
            collapseTimer.current = setTimeout(() => setCollapsed(true), 4000);
            return;
          }
          setOpen(true);
        }}
        className={[
          "fixed z-40 right-3 sm:right-5 group flex items-center justify-center rounded-full bg-[#1C1C1A] text-white shadow-[0_18px_40px_-14px_rgba(28,28,26,0.55)] hover:shadow-[0_20px_50px_-12px_rgba(124,139,110,0.55)]",
          "transition-all duration-400 ease-out",
          hidden ? "opacity-0 pointer-events-none translate-y-3" : "opacity-100 translate-y-0",
          collapsed ? "gap-0 p-1.5" : "gap-2.5 px-3.5 py-2.5 sm:px-4 sm:py-3",
        ].join(" ")}
        style={{
          // iter v29 r168 — Safe area iPhone home indicator
          bottom: "calc(20px + env(safe-area-inset-bottom))",
        }}
        data-testid="property-voice-fab"
        data-collapsed={collapsed ? "1" : "0"}
        aria-label="Comando vocale annuncio"
      >
        <span
          className={[
            "rounded-full bg-[#7C8B6E] flex items-center justify-center ring-2 ring-white/15 shrink-0",
            "transition-all duration-300",
            collapsed ? "w-10 h-10" : "w-9 h-9",
          ].join(" ")}
        >
          <Microphone size={collapsed ? 18 : 16} weight="fill" className="text-white" />
        </span>
        {/* iter v29 r099 — User M1500: «centrami il pulsante del microfono
            sul cerchio nero». Quando collapsed il testo restava nel DOM
            con max-w-0 + flex-col spostando l'icona. Soluzione: non
            rendere il blocco testo quando collapsed così il cerchio verde
            è UNICO figlio del flex e justify-center+items-center lo
            centra perfettamente sia mobile che desktop. */}
        {!collapsed && (
          <span className="hidden sm:flex flex-col text-left leading-tight whitespace-nowrap pr-1">
            <span className="text-[12.5px] font-semibold tracking-tight">
              Parla a Pronto
            </span>
            <span className="text-[10.5px] text-white/70">comandi · modifiche · foto</span>
          </span>
        )}
      </button>

      <VoiceCommandModal
        open={open}
        onClose={() => setOpen(false)}
        property={property}
        photos={photos}
        onUpdated={onUpdated}
      />
    </>
  );
}
